import { getCourseDatesFromCourseEnrolmentScheduleItems } from '@duke-hq/lib/dates';
import {
  isResourceStatus,
  RESOURCE_STATUSES,
  type RESOURCE_STATUSES_TYPE,
} from '@duke-hq/shared-constants/resourceStatuses';
import {
  StudentCourseEnrolmentStatus,
  Prisma,
  StudentScheduleItemType,
  StudentWorkSchedule,
} from '@duke-hq/svc-api-db/client';
import { djs } from '@duke-hq/djs';
import { groupBy } from 'lodash-es';

type RawScheduleItem = Prisma.StudentCourseEnrolmentScheduleItemGetPayload<{
  select: {
    started_at: true;
    item_type: true;
    is_complete: true;
    theoryCompleteAt: true;
    unit_of_competency_id: true;
    item_id: true;
    position: true;
    semester: true;
    scheduled_start_date: true;
    id: true;
    admin_course_schedule_week: true;
  };
}>;

interface UnitCompetencyResource {
  title: string | undefined;
  status: string | undefined;
  item_id: string;
  item_type: StudentScheduleItemType;
  // Optional on input (not every resource source provides it); always resolved
  // to number | null on the structured output below.
  submissionId?: number | null;
}

interface UnitCompetencyMap {
  [key: string]: {
    id: string;
    title: string | undefined;
    code: string;
    work_placement: boolean;
    resources: UnitCompetencyResource[];
  };
}

interface EnhancedScheduleItem extends RawScheduleItem {
  itemTitle: string | undefined;
  resourceStatus: RESOURCE_STATUSES_TYPE;
  submissionId: number | null;
}

interface ProcessedUnit {
  id: string;
  semester: number;
  semesterReleaseDate: Date;
  position: number;
  unitTitle: string | undefined;
  unitCode: string;
  overallStatus: RESOURCE_STATUSES_TYPE;
  scheduleItemDetails: EnhancedScheduleItem[];
  isLanguageLearningUnit: boolean;
  effectiveStartDate: Date;
  unitOfCompetencyId: string;
}

function hasRecognitionOfPriorLearning(items: EnhancedScheduleItem[]): boolean {
  return items.some(
    (item) => item.resourceStatus === RESOURCE_STATUSES.RECOGNITION_OF_PRIOR_LEARNING,
  );
}

function hasCreditTransfer(items: EnhancedScheduleItem[]): boolean {
  return items.some((item) => item.resourceStatus === RESOURCE_STATUSES.CREDIT_TRANSFER);
}

function hasNotRequired(items: EnhancedScheduleItem[]): boolean {
  return items.some((item) => item.resourceStatus === RESOURCE_STATUSES.NOT_REQUIRED);
}

/**
 * Returns SUBMITTED or REVISION if either appears on an assessment item,
 * otherwise returns null if not found.
 */
function getSubmissionOrRevisionStatus(
  items: EnhancedScheduleItem[],
): RESOURCE_STATUSES_TYPE | null {
  const found = items.find(
    (item) =>
      item.item_type === StudentScheduleItemType.ASSESSMENT &&
      (item.resourceStatus === RESOURCE_STATUSES.SUBMITTED ||
        item.resourceStatus === RESOURCE_STATUSES.REVISION),
  );
  return found ? found.resourceStatus : null;
}

function hasStartedAnyItem(items: EnhancedScheduleItem[]): boolean {
  return items.some((item) => Boolean(item.started_at));
}

function hasAnyInProgressItems(items: EnhancedScheduleItem[]) {
  return items.some((item) => item.started_at && !item.is_complete);
}

function hasCompletedAllItemsOrAssessment(items: EnhancedScheduleItem[]) {
  // It's invalid for there to be no items or assessments
  if (items.length === 0) {
    return false;
  }
  // For backward compatibility, if there is a completed assessment, the unit is always complete
  const hasCompletedAssessment = items.find(
    ({ item_type, is_complete }) => item_type === StudentScheduleItemType.ASSESSMENT && is_complete,
  );

  if (hasCompletedAssessment) {
    return true;
  }

  // Otherwise check if all items are complete
  return items.every(({ is_complete }) => is_complete);
}

function hasCompletedAllTheory(items: EnhancedScheduleItem[]) {
  if (items.length === 0) {
    return false;
  }

  const hasCompletedAssessmentTheory = items.find(
    ({ item_type, theoryCompleteAt }) =>
      item_type === StudentScheduleItemType.ASSESSMENT && theoryCompleteAt != null,
  );

  if (hasCompletedAssessmentTheory) {
    return true;
  }

  return items.every(({ theoryCompleteAt }) => theoryCompleteAt != null);
}

function deriveOverallUnitStatus(
  unitScheduleItems: EnhancedScheduleItem[],
  enrolmentStatus: StudentCourseEnrolmentStatus,
  hasIncompleteWorkPlacement: boolean,
): RESOURCE_STATUSES_TYPE {
  if (hasRecognitionOfPriorLearning(unitScheduleItems)) {
    return RESOURCE_STATUSES.RECOGNITION_OF_PRIOR_LEARNING;
  }

  if (hasCreditTransfer(unitScheduleItems)) {
    return RESOURCE_STATUSES.CREDIT_TRANSFER;
  }

  if (hasNotRequired(unitScheduleItems)) {
    return RESOURCE_STATUSES.NOT_REQUIRED;
  }

  if (hasCompletedAllItemsOrAssessment(unitScheduleItems)) {
    return RESOURCE_STATUSES.COMPLETED;
  }

  if (hasIncompleteWorkPlacement && hasCompletedAllTheory(unitScheduleItems)) {
    return RESOURCE_STATUSES.PENDING;
  }

  if (enrolmentStatus === StudentCourseEnrolmentStatus.PAUSED) {
    if (!hasAnyInProgressItems(unitScheduleItems)) {
      return RESOURCE_STATUSES.PAUSED;
    }
  }

  const subOrRev = getSubmissionOrRevisionStatus(unitScheduleItems);
  if (subOrRev) {
    return subOrRev;
  }

  if (hasStartedAnyItem(unitScheduleItems)) {
    return RESOURCE_STATUSES.IN_PROGRESS;
  }

  return RESOURCE_STATUSES.NOT_STARTED;
}

const extractUnitSpecificScheduleItems = (
  courseEnrolmentScheduleItems: RawScheduleItem[],
  unitCompetency: {
    id: string;
    resources: UnitCompetencyResource[];
  },
  assessmentsInRevisionSet: Set<string>,
): EnhancedScheduleItem[] => {
  return courseEnrolmentScheduleItems
    .filter(
      (scheduleItem) =>
        scheduleItem.item_type !== StudentScheduleItemType.UNIT &&
        scheduleItem.unit_of_competency_id === unitCompetency.id,
    )
    .map((unitScheduleItem) => {
      const matchingResource = unitCompetency.resources.find(
        (resource) => resource.item_id === unitScheduleItem.item_id,
      );

      if (!matchingResource) {
        return null;
      }

      let resourceStatus = isResourceStatus(matchingResource.status)
        ? matchingResource.status
        : RESOURCE_STATUSES.NOT_STARTED;

      if (
        matchingResource.item_type === StudentScheduleItemType.ASSESSMENT &&
        matchingResource.status === RESOURCE_STATUSES.IN_PROGRESS &&
        assessmentsInRevisionSet.has(unitScheduleItem.unit_of_competency_id)
      ) {
        resourceStatus = RESOURCE_STATUSES.REVISION;
      }

      return {
        itemTitle: matchingResource.title,
        resourceStatus,
        submissionId: matchingResource.submissionId ?? null,
        ...unitScheduleItem,
      };
    })
    .filter((unitItem): unitItem is EnhancedScheduleItem => unitItem !== null)
    .sort((a, b) => a.position - b.position);
};

export const getStructuredUnitOfCompetencies = (
  courseEnrolmentScheduleItems: RawScheduleItem[],
  unitCompetencyMap: UnitCompetencyMap,
  assessmentsInRevisionSet: Set<string>,
  courseEnrolment: {
    created_at: Date;
    start_at_date: Date;
    semester_2_start_at_date: Date | null;
    course_segment: string;
    status: StudentCourseEnrolmentStatus;
    work_schedule: StudentWorkSchedule;
  },
  completedWorkPlacementUocIds: string[] = [],
): Record<string, ProcessedUnit[]> => {
  const courseStartAndEndDates = getCourseDatesFromCourseEnrolmentScheduleItems({
    courseEnrolmentScheduleItems: courseEnrolmentScheduleItems,
    courseSegment: courseEnrolment.course_segment,
    workSchedule: courseEnrolment.work_schedule,
    startAt: courseEnrolment.start_at_date,
    semesterTwoStartAt: courseEnrolment.semester_2_start_at_date,
  });
  const semesterTwoStartDate = courseStartAndEndDates.semesters.find((item) => item.semester === 2);
  const processedUnits = courseEnrolmentScheduleItems
    .filter((scheduleItem) => scheduleItem.item_type === StudentScheduleItemType.UNIT)
    .map((scheduleItem) => {
      const currentUnitCompetency = unitCompetencyMap[scheduleItem.unit_of_competency_id];

      if (currentUnitCompetency === undefined) {
        return null;
      }

      const isLanguageLearningUnit = scheduleItem.semester === 0;
      const unitSpecificScheduleItems = extractUnitSpecificScheduleItems(
        courseEnrolmentScheduleItems,
        currentUnitCompetency,
        assessmentsInRevisionSet,
      );

      const hasIncompleteWorkPlacement =
        currentUnitCompetency.work_placement &&
        !completedWorkPlacementUocIds.includes(scheduleItem.unit_of_competency_id);

      const overallUnitStatus = deriveOverallUnitStatus(
        unitSpecificScheduleItems,
        courseEnrolment.status,
        hasIncompleteWorkPlacement,
      );

      const effectiveStartDate = scheduleItem.scheduled_start_date;

      let semesterReleaseDate = courseEnrolment.start_at_date;

      if (scheduleItem.semester === 0) {
        semesterReleaseDate = djs(scheduleItem.scheduled_start_date).toDate();
      }

      if (scheduleItem.semester === 2 && semesterTwoStartDate) {
        semesterReleaseDate =
          courseEnrolment.semester_2_start_at_date ||
          semesterTwoStartDate.semesterStartDate.rawDate; //TODO - Lucas see if we can avoid using the rawDate here
      }

      return {
        id: scheduleItem.id,
        semester: scheduleItem.semester,
        semesterReleaseDate,
        position: scheduleItem.position,
        unitTitle: currentUnitCompetency.title,
        unitCode: currentUnitCompetency.code,
        overallStatus: overallUnitStatus,
        scheduleItemDetails: unitSpecificScheduleItems,
        isLanguageLearningUnit,
        effectiveStartDate,
        unitOfCompetencyId: scheduleItem.unit_of_competency_id,
      };
    })
    .filter((unit): unit is ProcessedUnit => unit !== null);

  return groupBy(processedUnits, 'semester');
};
